import { ClearOutlined } from '@mui/icons-material';
import { Autocomplete, Chip, IconButton, styled, TextField, Tooltip } from '@mui/material';
import { clone, cloneDeep, filter, find, findIndex, map } from 'lodash';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import useDebounce from '../../hooks/useDebounce';
import { FuelPortalService } from '../../services/fuel-portal/fuel-portal-service';
import { applicationDataSelector } from '../../store/slices/application-data';
import useAppUtils from '../../utils/appUtils';
import itemUtils from '../../utils/itemUtils';
import FuelConfirmDialog from '../fuel-confirm-dialog';

interface IProps {
  id: string;
  tags: Array<string>;
  inputTagWidth: number;
  onChange?: (tags: []) => void;
  labelText?: string;
  excludedTags?: string[];
  disabled?: boolean;
  /** When provided, tag suggestions are fetched from all listed OU IDs and merged (doc_counts summed). Falls back to the current OU when empty. */
  ouIds?: string[];
  /** When true, show a clear-all button (2+ tags) with tooltip "Clear All Tags". Per-chip delete still works. */
  allowClear?: boolean;
}
const StyledAutocomplete = styled(Autocomplete)({
  '& .MuiAutocomplete-inputRoot': {
    //   color: "purple",
    // This matches the specificity of the default styles at https://github.com/mui-org/material-ui/blob/v4.11.3/packages/material-ui-lab/src/Autocomplete/Autocomplete.js#L90
    '&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input': {
      // Default left padding is 6px
      // paddingLeft: 26
      // height: '16px',
      // padding: '2px'
    },
    //   "& .MuiOutlinedInput-notchedOutline": {
    //     borderColor: "green"
    //   },
    //   "&:hover .MuiOutlinedInput-notchedOutline": {
    //     borderColor: "red"
    //   },
    //   "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    //     borderColor: "purple"
    //   }

    '&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-tag': {
      margin: 0,
      marginRight: '3px',
      maxWidth: '175px',
    },
  },
  '& .MuiAutocomplete-root': {
    '& .MuiFormControl-root': {
      '&[class*="MuiInputLabel-root"] .MuiInputLabel-formControl': {
        top: '5px',
      },
    },
  },
});

const FuelTags = ({
  id,
  tags,
  inputTagWidth,
  onChange,
  labelText = 'Select Tags',
  excludedTags = [],
  disabled = false,
  ouIds = [],
  allowClear = false,
}: IProps) => {
  const { ouData } = useSelector(applicationDataSelector);
  const [inputTag, setInputTag] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [value, setValue] = useState(tags);
  const [open, setOpen] = useState<boolean>(false);
  const [tag, setTag] = useState<string>('');
  const debouncedValue = useDebounce<string>(tag, 500);
  const [oldTag, setOldTag] = useState<string>('');
  const isClearingRef = useRef(false);
  const { alert } = useAppUtils();

  const handleTagInput = async (event: React.SyntheticEvent, newTagInput: string) => {
    setTag(newTagInput);
    const options = newTagInput.split(/[,]+/);

    const fieldValue = value
      .concat(options)
      .map((x) => x.trim())
      .filter((x) => x);

    if (options.length > 1) {
      handleChange(event, fieldValue);
    } else {
      setInputTag(newTagInput);
    }
  };

  const handleChange = (event: React.ChangeEvent<{}>, tags: string[]) => {
    const freshTags = refreshTags(tags);
    setValue(freshTags);
    event.persist?.();
    setIsUpdating(false);
    onChange(freshTags);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // console.log('event', event.key);
    switch (event.key) {
      // 2022-07-13 aalt prevent all commas and backslashes in tags names
      case ',':
      case '\\': {
        event.preventDefault();
        alert('Commas and backslashes not allowed in tag names.', 'warning');
        break;
      }
      case 'Tab':
      case 'Enter': {
        if (inputTag.length > 0) {
          handleChange(event, value.concat([inputTag]));
          handleTagInput(event, '');
        }
        break;
      }
      default:
    }
  };
  const refreshTags = (tags: string[]) => {
    setIsUpdating(true);

    const clonedTags = clone(tags);
    const tagsCopy = itemUtils.cleanupTags(clonedTags);

    // let tagsCopy = filter(
    //   uniq(
    //     map(clone(tags), (t) => {
    //       return itemUtils.isMetaTag(t) ? itemUtils.getSafeMetaTagString(t) : t.trim().toLowerCase();
    //     })
    //   ),
    //   size
    // );

    setTimeout(() => {
      setIsUpdating(false);
    }, 30);
    return tagsCopy;
  };

  const handleTagDoubleClick = (event, tag: string) => {
    switch (event.detail) {
      case 1:
        // console.log('click');
        break;
      case 2:
        // console.log('double click');
        openEditTagDialog(tag);
        break;
      case 3:
        // console.log('triple click');
        break;
    }
  };

  const openEditTagDialog = (tag: string) => {
    // console.log('Edit tag value', tag);
    setTag(tag);
    setOldTag(tag);
    setOpen(true);
  };

  const editTag = (isOk) => {
    if (!isOk) {
      return;
    }
    let newValue = tag;
    if (itemUtils.isMetaTag(newValue)) {
      newValue = itemUtils.getSafeMetaTagString(newValue);
    } else {
      newValue = itemUtils.getSafeTagString(newValue);
    }

    const index = findIndex(value, (t) => t == oldTag);
    if (index > -1) {
      const tempTags = clone(value);
      tempTags.splice(index, 1, newValue);
      updateTagsWithValue(tempTags);
    }
  };
  const updateTagsWithValue = (items: string[]) => {
    if (!isUpdating) {
      setIsUpdating(true);
      const oldVal = clone(value);
      const freshTags = refreshTags(items);
      setValue(freshTags);
      onChange(freshTags);
    }
  };
  const handleTagChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTag((event.target as HTMLInputElement).value);
  };

  const handlePaste = (e) => {
    // console.log('onPaste', e);
  };

  const getSearchResults = (searchText: string) => {
    const fallbackOuId = ouData?.ouId ?? ouData?.id;
    const effectiveOuIds = ouIds.length > 0 ? ouIds : fallbackOuId ? [fallbackOuId] : [];
    if (searchText.length === 0 || effectiveOuIds.length === 0) {
      return;
    }
    setSearchResults([]);
    setSuggestedTags([]);

    Promise.all(effectiveOuIds.map((ouId) => FuelPortalService.searchTags(ouId, searchText)))
      .then((perOuResults) => {
        // Flatten and translate to { key, doc_count } format, then sum doc_counts across OUs
        const countMap = new Map<string, number>();
        perOuResults.forEach((tags) => {
          tags.forEach((tag) => {
            if (tag.item1) {
              countMap.set(tag.item1, (countMap.get(tag.item1) ?? 0) + tag.item2);
            }
          });
        });

        let adjustedResults = Array.from(countMap.entries()).map(([key, doc_count]) => ({ key, doc_count }));
        adjustedResults = filter(adjustedResults, (i) => {
          return !value.includes(i.key) && !excludedTags.includes(i.key);
        });
        setSearchResults(adjustedResults);
        setSuggestedTags(
          map(adjustedResults, (p) => {
            return p.key;
          })
        );
      })
      .catch((err) => {
        console.error('Unable to get tags due to', err);
      });
  };

  const getTagLabel = (tag: string) => {
    const tagObject = find(searchResults, { key: tag });
    const tagCount = tagObject ? tagObject.doc_count : null;
    return tagCount ? `${tag} (${tagCount})` : tag;
  };

  const onDeleteTag = (event, option, index) => {
    // console.log('onDeleteTag', { index, event, option, value });
    const newValues = cloneDeep(value);
    newValues.splice(index, 1);
    setValue(newValues);
    onChange(newValues);
  };

  const handleClearAll = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    isClearingRef.current = true;
    handleChange(event as unknown as React.ChangeEvent<{}>, []);
    queueMicrotask(() => {
      isClearingRef.current = false;
    });
  };

  const showClearAll = allowClear && !disabled && (value?.length ?? 0) >= 2;
  const hasTags = (value?.length ?? 0) > 0;

  const renderTags = (value: string[], getTagProps) => {
    // console.log('renderTags', { value });
    return value.map((option: string, index: number) => {
      const otherOptions = getTagProps({ index });
      // console.log('renderTags', { otherOptions });
      if (otherOptions) {
        delete otherOptions.onDelete;
        delete otherOptions.key; // solve React Warning: A props object containing a "key" prop is being spread into JSX:
        if (otherOptions.className) {
          otherOptions.className += ' tag-chip';
          if (itemUtils.isMetaTag(option)) otherOptions.className += ' meta';
          if (labelText === 'Excluding') otherOptions.className += ' exclude';
        }
      }
      return (
        <Chip
          key={index}
          clickable
          size="small"
          onClick={(e) => handleTagDoubleClick(e, option)}
          title={option}
          label={option}
          onDelete={(e) => onDeleteTag(e, option, index)}
          {...otherOptions}
        />
      );
    });
  };

  // Stable string key so the effect re-runs whenever the OU selection changes,
  // even if the tag search text hasn't changed.
  const ouIdsKey = ouIds.join(',');

  useEffect(() => {
    // Do fetch here...
    // Triggers when "debouncedValue" changes
    // console.log('call', debouncedValue);
    if (debouncedValue && debouncedValue.length > 2) {
      getSearchResults(debouncedValue);
    } else {
      setSearchResults([]);
      setSuggestedTags([]);
    }
  }, [debouncedValue, ouIdsKey]);

  useEffect(() => {
    if (isClearingRef.current) {
      return;
    }
    setValue(tags);
  }, [tags]);

  return (
    <div
      className={[
        'fuel-tags',
        'vn-dropdown-trigger',
        hasTags ? 'fuel-tags--has-value' : '',
        showClearAll ? 'fuel-tags--with-clear' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <StyledAutocomplete
        id={id}
        disableClearable
        forcePopupIcon={false}
        options={suggestedTags}
        getOptionLabel={(option) => getTagLabel(option)}
        value={value || []}
        inputValue={inputTag !== null ? inputTag : ''}
        multiple
        freeSolo
        size="small"
        limitTags={2}
        getLimitTagsText={(more) => <span className="fuel-tags__limit-badge">+{more}</span>}
        onChange={(event, newValue) => handleChange(event, newValue)}
        onInputChange={handleTagInput}
        renderTags={(value: string[], getTagProps) => renderTags(value, getTagProps)}
        componentsProps={{
          popper: {
            sx: { zIndex: 1600 },
          },
        }}
        sx={{ width: inputTagWidth ?? '100%' }}
        renderInput={(params) => {
          params.inputProps.onKeyDown = handleKeyDown;
          return (
            <TextField
              fullWidth
              placeholder="Type to find tags..."
              {...params}
              label={labelText}
              InputProps={{
                ...params.InputProps,
                endAdornment: null,
              }}
            />
          );
        }}
        disabled={disabled}
      />
      {showClearAll && (
        <Tooltip title="Clear All Tags">
          <IconButton
            size="small"
            className="vn-dropdown-display__clear-btn fuel-tags__clear-all"
            aria-label="Clear All Tags"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={handleClearAll}
          >
            <ClearOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <FuelConfirmDialog title="" open={open} setOpen={setOpen} onConfirm={(e) => editTag(e)}>
        <TextField
          variant="outlined"
          label="Edit Tag"
          value={tag}
          fullWidth
          onChange={handleTagChange}
          onPaste={(e) => handlePaste}
        />
      </FuelConfirmDialog>
    </div>
  );
};

export default FuelTags;
